import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ filters, onFiltersChange, isOpen, onToggle, resultCount }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const categories = [
    { id: 'education', label: 'Education', count: 234 },
    { id: 'health', label: 'Health & Medical', count: 189 },
    { id: 'environment', label: 'Environment', count: 156 },
    { id: 'poverty', label: 'Poverty & Hunger', count: 298 },
    { id: 'disaster', label: 'Disaster Relief', count: 87 },
    { id: 'animals', label: 'Animal Welfare', count: 123 },
    { id: 'human-rights', label: 'Human Rights', count: 167 },
    { id: 'arts', label: 'Arts & Culture', count: 92 }
  ];

  const locations = [
    { id: 'local', label: 'Local (Within 50 miles)', count: 145 },
    { id: 'national', label: 'National', count: 567 },
    { id: 'international', label: 'International', count: 423 }
  ];

  const impactSizes = [
    { id: 'small', label: 'Small Organizations (<$100K)', count: 234 },
    { id: 'medium', label: 'Medium Organizations ($100K-$1M)', count: 345 },
    { id: 'large', label: 'Large Organizations (>$1M)', count: 189 }
  ];

  const trustLevels = [
    { id: 'verified', label: 'Verified Organizations', count: 456 },
    { id: 'transparent', label: 'High Transparency Score', count: 234 },
    { id: 'efficient', label: 'High Efficiency Rating', count: 345 }
  ];

  const handleFilterChange = (type, value, checked) => {
    const newFilters = { ...localFilters };
    
    if (type === 'search') {
      newFilters.search = value;
    } else {
      if (!newFilters?.[type]) newFilters[type] = [];
      
      if (checked) {
        newFilters[type] = [...newFilters?.[type], value];
      } else {
        newFilters[type] = newFilters?.[type]?.filter(item => item !== value);
      }
    }
    
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = { search: '', categories: [], locations: [], impactSizes: [], trustLevels: [] };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters?.search) count++;
    if (localFilters?.categories?.length) count += localFilters?.categories?.length;
    if (localFilters?.locations?.length) count += localFilters?.locations?.length;
    if (localFilters?.impactSizes?.length) count += localFilters?.impactSizes?.length;
    if (localFilters?.trustLevels?.length) count += localFilters?.trustLevels?.length;
    return count;
  };

  const FilterSection = ({ title, items, type, icon }) => (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Icon name={icon} size={16} className="text-muted-foreground" />
        <h3 className="font-medium text-foreground">{title}</h3>
      </div>
      <div className="space-y-2">
        {items?.map((item) => (
          <Checkbox
            key={item?.id}
            label={
              <div className="flex items-center justify-between w-full">
                <span className="text-sm">{item?.label}</span>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  {item?.count}
                </span>
              </div>
            }
            checked={localFilters?.[type]?.includes(item?.id) || false}
            onChange={(e) => handleFilterChange(type, item?.id, e?.target?.checked)}
            className="w-full"
          />
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="md:hidden mb-4">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          className="w-full"
        >
          Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
        </Button>
      </div>
      {/* Filter Panel */}
      <div className={`
        ${isOpen ? 'block' : 'hidden'} md:block
        fixed md:relative inset-0 md:inset-auto z-50 md:z-auto
        bg-background md:bg-transparent
        overflow-y-auto md:overflow-visible
      `}>
        {/* Mobile Overlay */}
        <div className="md:hidden fixed inset-0 bg-black/50" onClick={onToggle} />
        
        {/* Filter Content */}
        <div className="relative md:static bg-card border-0 md:border border-border rounded-none md:rounded-lg p-6 h-full md:h-auto max-w-sm md:max-w-none mx-auto md:mx-0 mt-16 md:mt-0">
          {/* Mobile Header */}
          <div className="md:hidden flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-card-foreground">Filters</h2>
            <Button variant="ghost" size="sm" onClick={onToggle}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Search */}
          <div className="mb-6">
            <Input
              type="search"
              placeholder="Search causes, organizations..."
              value={localFilters?.search || ''}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Filter Sections */}
          <div className="space-y-6">
            <FilterSection
              title="Categories"
              items={categories}
              type="categories"
              icon="Tag"
            />
            
            <FilterSection
              title="Location"
              items={locations}
              type="locations"
              icon="MapPin"
            />
            
            <FilterSection
              title="Organization Size"
              items={impactSizes}
              type="impactSizes"
              icon="Building"
            />
            
            <FilterSection
              title="Trust & Verification"
              items={trustLevels}
              type="trustLevels"
              icon="Shield"
            />
          </div>

          {/* Clear Filters */}
          {getActiveFilterCount() > 0 && (
            <div className="mt-6 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={clearAllFilters}
                iconName="X"
                iconPosition="left"
                className="w-full"
              >
                Clear All Filters
              </Button>
            </div>
          )}

          {/* Results Count */}
          <div className="mt-6 p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground text-center">
              <span className="font-medium text-foreground">{resultCount}</span> organizations found
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;